import React, { AnchorHTMLAttributes, forwardRef, useCallback, useMemo } from 'react';

import { NavLink, NavLinkProps } from 'react-router-dom';
import classNames from 'classnames';
import './NavigationLink.scss';
import { useLocation } from 'react-router';

type InternalLinkProps = NavLinkProps & {
    /**
     * Indicates that the link points to an internal route.
     * This causes the link to use React Router for handling navigation.
     */
    readonly external?: false;

    /**
     * A list of routes for which the link should also be considered active.
     *
     * For example the shop link points to '/catalogue' but it must also be rendered as active when
     * the user is browsing campaigns which are located at '/unlimited/...'.
     */
    readonly alternativeRoutes?: ReadonlyArray<string>;
};

type ExternalLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
    /**
     * Indicates that the link points to an external target.
     * This causes the link to bypass React Router and instead render a normal <a> tag.
     */
    readonly external: true;

    /**
     * Alternative routes are not supported for external links.
     */
    readonly alternativeRoutes?: undefined;
};

export type NavigationLinkProps = InternalLinkProps | ExternalLinkProps;

/**
 * A thin wrapper for React Routers NavLink, which provides to ability to be active for more than one route.
 * Additionally it can also handle rendering external links, such as links to catalogue management.
 */
export const NavigationLink = forwardRef<HTMLAnchorElement, NavigationLinkProps>((props, ref) => {
    if (props.external) {
        return <ExternalNavLink {...props} ref={ref} />;
    }

    return <InternalNavLink {...props} ref={ref} />;
});

const InternalNavLink = forwardRef<HTMLAnchorElement, InternalLinkProps>((props, ref) => {
    const { className, external, alternativeRoutes, ...navLinkProps } = props;

    const { pathname } = useLocation();

    const alternativeActive = useMemo(() => {
        if (!alternativeRoutes) {
            return false;
        }
        return !!alternativeRoutes.find(alternativeRoute => pathname.startsWith(alternativeRoute));
    }, [alternativeRoutes, pathname]);

    const classNameFunc = useCallback(
        (props: { isActive: boolean; isPending: boolean; isTransitioning: boolean }) => {
            return typeof className === 'function'
                ? classNames('navigation-link', className(props))
                : classNames('navigation-link', { active: props.isActive || alternativeActive }, className);
        },
        [alternativeActive, className]
    );

    return <NavLink {...navLinkProps} ref={ref} className={classNameFunc} />;
});

const ExternalNavLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>((props, ref) => {
    const { className, external, alternativeRoutes, ...anchorProps } = props;
    return <a {...anchorProps} ref={ref} className={classNames('navigation-link', className)} />;
});
